/**
 * Shipments tab for the Order Detail page.
 * Renders a list of shipment cards showing contact, address, shipping method,
 * tracking info, delivery date, and an expandable items accordion per shipment.
 */
import React from "react";
import { Box, createStyles, Skeleton } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { useOutletContext } from "react-router-dom";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { Body } from "../../../components/Typography";
import { ShipmentCard } from "./shipments/ShipmentCard";
import { OrderViewOutletContext } from "./order-details/shared";

const useStyles = createStyles({
  container: {
    padding: tokens.spacing[4],
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[6],
  },
  emptyState: {
    padding: tokens.spacing[4],
  },
});

export const OrderShipmentsTab: React.FC = () => {
  const { classes } = useStyles();
  const { customerPublicId, customerOrderId } = useOutletContext<OrderViewOutletContext>();
  const user = useAuthUser();
  const { data, isLoading } = GraphQL.useOrderShipmentsQuery({
    orderIdentifier: {
      customerPublicId,
      customerOrderId,
    },
    isSandbox: user.isSandbox,
  });

  if (isLoading) {
    return (
      <Box data-testid="order-shipments-loading" className={classes.container}>
        <Skeleton height={220} radius="lg" />
        <Skeleton height={220} radius="lg" />
      </Box>
    );
  }

  const order = data?.orderByIdentifier;
  if (!order) return null;

  const shipments = order.shipments ?? [];
  const currency = order.currency ?? GraphQL.SupportedCurrency.Usd;

  if (shipments.length === 0) {
    return (
      <Box data-testid="order-shipments-loaded" className={classes.emptyState}>
        <Body>No shipments found for this order.</Body>
      </Box>
    );
  }

  return (
    <Box data-testid="order-shipments-loaded" className={classes.container}>
      {shipments.map((shipment, index) => (
        <ShipmentCard key={index} shipment={shipment} currency={currency} isSuperAdmin={user.is.SUPER_ADMIN} />
      ))}
    </Box>
  );
};
